import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import {
  BuildingOfficeIcon,
  UsersIcon,
  CogIcon,
  ChartBarIcon,
  PlusIcon,
  PencilIcon,
  TrashIcon,
  MagnifyingGlassIcon,
  EnvelopeIcon,
  XMarkIcon,
  ArrowPathIcon,
  BellAlertIcon,
} from '@heroicons/react/24/outline';
import { organizationApi, userApi, Organization, User } from '@/services/api';
import { invitationApi, roleApi, Invitation } from '@/services/roleApi';
import { PermissionGuard } from '@/components/Auth/PermissionGuard';
import { usePermissions } from '@/hooks/usePermissions';
import { useAuth } from '@/hooks/useAuth';

const OrganizationPage: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'overview' | 'members' | 'invitations' | 'settings'>('overview');
  const [showAddMember, setShowAddMember] = useState(false);
  const [showEditOrg, setShowEditOrg] = useState(false);
  const [showNewOrganization, setShowNewOrganization] = useState(false);
  const [newOrgName, setNewOrgName] = useState('');
  const [newOrgCountry, setNewOrgCountry] = useState('');
  const [memberSearch, setMemberSearch] = useState('');
  const [memberRole, setMemberRole] = useState('');
  const [searchParams, setSearchParams] = useSearchParams();

  const queryClient = useQueryClient();
  const { hasPermission } = usePermissions();
  const { createOrganization, isLoading } = useAuth();

  useEffect(() => {
    const tab = searchParams.get('tab');
    if (tab === 'new-organization') {
      setShowNewOrganization(true);
    }
  }, [searchParams]);

  // Queries
  const { data: organization, isLoading: orgLoading } = useQuery({
    queryKey: ['organization'],
    queryFn: () => organizationApi.getCurrent().then(res => res.data.data),
  });

  const { data: orgStats, isLoading: statsLoading } = useQuery({
    queryKey: ['organization-stats'],
    queryFn: () => organizationApi.getStats().then(res => res.data.data),
  });

  const { data: membersData, isLoading: membersLoading } = useQuery({
    queryKey: ['organization-members', memberSearch, memberRole],
    queryFn: () => organizationApi.getMembers({
      search: memberSearch || undefined,
      role: memberRole || undefined,
      limit: 50,
    }).then(res => res.data.data),
  });

  const { data: rolesData } = useQuery({
    queryKey: ['org-roles'],
    queryFn: () => roleApi.getAll({ limit: 50, isActive: true }).then(res => res.data.data),
  });

  const { data: invitationsData, isLoading: invitationsLoading } = useQuery({
    queryKey: ['org-invitations'],
    queryFn: () => invitationApi.getAll({ limit: 50 }).then(res => res.data.data),
    enabled: activeTab === 'invitations',
  });

  const { data: usersData } = useQuery({
    queryKey: ['users-simple'],
    queryFn: () => userApi.getAll({ limit: 100 }).then(res => res.data.data),
  });

  // Mutations
  const updateOrgMutation = useMutation({
    mutationFn: (data: Partial<Organization>) => organizationApi.update(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['organization'] });
      toast.success('Organization updated successfully');
      setShowEditOrg(false);
    },
    onError: () => toast.error('Failed to update organization'),
  });

  const sendInviteMutation = useMutation({
    mutationFn: (data: { email: string; roleId: string; message?: string }) =>
      invitationApi.create(data),
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: ['org-invitations'] });
      toast.success(res.data.message || 'Invitation sent!');
      setShowAddMember(false);
    },
    onError: (err: any) => toast.error(err.response?.data?.message || 'Failed to send invitation'),
  });

  const resendInviteMutation = useMutation({
    mutationFn: (id: string) => invitationApi.resend(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['org-invitations'] });
      toast.success('Invitation resent');
    },
    onError: () => toast.error('Failed to resend invitation'),
  });

  const cancelInviteMutation = useMutation({
    mutationFn: (id: string) => invitationApi.cancel(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['org-invitations'] });
      toast.success('Invitation cancelled');
    },
    onError: () => toast.error('Failed to cancel invitation'),
  });

  const removeMemberMutation = useMutation({
    mutationFn: (userId: string) => organizationApi.removeMember(userId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['organization-members'] });
      toast.success('Member removed successfully');
    },
    onError: () => toast.error('Failed to remove member'),
  });

  const updateRoleMutation = useMutation({
    mutationFn: ({ userId, roleId }: { userId: string; roleId: string }) =>
      organizationApi.updateMember(userId, { roleId }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['organization-members'] });
      toast.success('Role updated successfully');
    },
    onError: () => toast.error('Failed to update role'),
  });

  const handleCreateOrganization = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newOrgName.trim()) {
      toast.error('Organization name is required');
      return;
    }
    if (!newOrgCountry.trim()) {
      toast.error('Country is required');
      return;
    }
    try {
      await createOrganization(newOrgName.trim(), newOrgCountry.trim());
      setNewOrgName('');
      setNewOrgCountry('');
      setShowNewOrganization(false);
      setSearchParams({});
      queryClient.invalidateQueries({ queryKey: ['organization'] });
      queryClient.invalidateQueries({ queryKey: ['organization-stats'] });
    } catch {
      // error shown by createOrganization
    }
  };

  const tabs = [
    ...(hasPermission('organizations', 'read') ? [{ id: 'overview', name: 'Overview', icon: ChartBarIcon }] : []),
    ...(hasPermission('users', 'read') ? [{ id: 'members', name: 'Members', icon: UsersIcon }] : []),
    ...(hasPermission('invitations', 'read') ? [{ id: 'invitations', name: 'Invitations', icon: EnvelopeIcon }] : []),
    ...(hasPermission('organizations', 'update') ? [{ id: 'settings', name: 'Settings', icon: CogIcon }] : []),
  ];

  const statusColors = {
    active: 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300',
    inactive: 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-300',
    suspended: 'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300',
  };

  const roleColors: Record<string, string> = {
    Owner: 'bg-purple-100 dark:bg-purple-900/30 text-purple-800 dark:text-purple-300',
    Admin: 'bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300',
    Manager: 'bg-indigo-100 dark:bg-indigo-900/30 text-indigo-800 dark:text-indigo-300',
    Member: 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300',
    Viewer: 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-300',
    User: 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-300',
  };

  if (orgLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white dark:bg-gray-800 shadow rounded-lg">
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <BuildingOfficeIcon className="h-8 w-8 text-blue-600 dark:text-blue-400" />
              <div>
                <h1 className="text-2xl font-bold text-gray-900 dark:text-white">{organization?.name}</h1>
                <p className="text-sm text-gray-500 dark:text-gray-400">{organization?.description}</p>
              </div>
            </div>
            <div className="flex items-center space-x-3">
              <button
                onClick={() => setShowNewOrganization(true)}
                className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700"
              >
                <PlusIcon className="h-4 w-4 mr-2" />
                New Organization
              </button>
              <button
                onClick={() => setShowEditOrg(true)}
                className="inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700"
              >
                <PencilIcon className="h-4 w-4 mr-2" />
                Edit Organization
              </button>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="border-b border-gray-200 dark:border-gray-700">
          <nav className="-mb-px flex space-x-8 px-6">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id as any)}
                  className={`py-4 px-1 border-b-2 font-medium text-sm flex items-center space-x-2 ${
                    activeTab === tab.id
                      ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                      : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300 dark:hover:border-gray-600'
                  }`}
                >
                  <Icon className="h-4 w-4" />
                  <span>{tab.name}</span>
                </button>
              );
            })}
          </nav>
        </div>
      </div>

      {/* Tab Content */}
      {activeTab === 'overview' && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Organization Info */}
          <div className="lg:col-span-2 bg-white dark:bg-gray-800 shadow rounded-lg p-6">
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">Organization Details</h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-500 dark:text-gray-400">Industry</label>
                <p className="mt-1 text-sm text-gray-900 dark:text-white">{organization?.industry || 'Not specified'}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-500 dark:text-gray-400">Size</label>
                <p className="mt-1 text-sm text-gray-900 dark:text-white capitalize">{organization?.size || 'Not specified'}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-500 dark:text-gray-400">Website</label>
                <p className="mt-1 text-sm text-gray-900 dark:text-white">{organization?.website || 'Not specified'}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-500 dark:text-gray-400">Subscription</label>
                <div className="mt-1 flex items-center space-x-2">
                  <span className="text-sm text-gray-900 dark:text-white capitalize">{organization?.subscription.plan}</span>
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                    statusColors[organization?.subscription.status || 'inactive']
                  }`}>
                    {organization?.subscription.status}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Statistics */}
          <div className="space-y-4">
            <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-6">
              <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">Statistics</h3>
              <div className="space-y-4">
                <div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-gray-500 dark:text-gray-400">Members</span>
                    <span className="text-2xl font-bold text-blue-600 dark:text-blue-400">{organization?.memberCount || 0}</span>
                  </div>
                </div>
                <div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-gray-500 dark:text-gray-400">Projects</span>
                    <span className="text-2xl font-bold text-green-600 dark:text-green-400">{organization?.projectCount || 0}</span>
                  </div>
                </div>
                {orgStats && (
                  <>
                    <div>
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-medium text-gray-500 dark:text-gray-400">Active Projects</span>
                        <span className="text-2xl font-bold text-orange-600 dark:text-orange-400">{orgStats.activeProjects || 0}</span>
                      </div>
                    </div>
                    <div>
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-medium text-gray-500 dark:text-gray-400">Completed</span>
                        <span className="text-2xl font-bold text-purple-600 dark:text-purple-400">{orgStats.completedProjects || 0}</span>
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {activeTab === 'members' && (
        <div className="bg-white dark:bg-gray-800 shadow rounded-lg">
          <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-medium text-gray-900 dark:text-white">Team Members</h3>
              <PermissionGuard module="invitations" action="create">
                <button
                  onClick={() => setShowAddMember(true)}
                  className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700"
                >
                  <PlusIcon className="h-4 w-4 mr-2" />
                  Invite Member
                </button>
              </PermissionGuard>
            </div>
            
            {/* Filters */}
            <div className="mt-4 flex space-x-4">
              <div className="flex-1">
                <div className="relative">
                  <MagnifyingGlassIcon className="h-5 w-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Search members..."
                    value={memberSearch}
                    onChange={(e) => setMemberSearch(e.target.value)}
                    className="pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-md focus:ring-blue-500 focus:border-blue-500 w-full"
                  />
                </div>
              </div>
              <select
                value={memberRole}
                onChange={(e) => setMemberRole(e.target.value)}
                className="px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-md focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="">All Roles</option>
                <option value="owner">Owner</option>
                <option value="admin">Admin</option>
                <option value="member">Member</option>
                <option value="viewer">Viewer</option>
              </select>
            </div>
          </div>

          {/* Members List */}
          <div className="divide-y divide-gray-200 dark:divide-gray-700">
            {membersLoading ? (
              <div className="p-6 text-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
              </div>
            ) : (
              membersData?.users?.map((member: User) => (
                <div key={member._id} className="px-6 py-4 flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <div className="h-10 w-10 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
                      <span className="text-sm font-medium text-blue-600 dark:text-blue-400">
                        {member.name.charAt(0).toUpperCase()}
                      </span>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-900 dark:text-white">{member.name}</p>
                      <p className="text-sm text-gray-500 dark:text-gray-400">{member.email}</p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-3">
                    {member.role === 'Owner' ? (
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${roleColors['Owner']}`}>
                        Owner
                      </span>
                    ) : (
                      <PermissionGuard module="users" action="update" fallback={
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${roleColors[member.role] || 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-300'}`}>
                          {member.role}
                        </span>
                      }>
                        <select
                          value={member.roleId || ''}
                          onChange={(e) => updateRoleMutation.mutate({ userId: member._id, roleId: e.target.value })}
                          disabled={updateRoleMutation.isLoading || !rolesData?.roles?.length}
                          className="text-xs border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded px-2 py-1 focus:ring-blue-500 focus:border-blue-500"
                        >
                          <option value="" disabled>Select role</option>
                          {(rolesData?.roles || []).filter((r: any) => r.name !== 'Owner').map((r: any) => (
                            <option key={r._id} value={r._id}>{r.name}</option>
                          ))}
                        </select>
                      </PermissionGuard>
                    )}
                    {member.role !== 'Owner' && (
                      <PermissionGuard module="users" action="delete">
                        <button
                          onClick={() => {
                            if (window.confirm(`Remove ${member.name || member.email} from this organization?`)) {
                              removeMemberMutation.mutate(member._id);
                            }
                          }}
                          disabled={removeMemberMutation.isLoading}
                          className="text-red-600 dark:text-red-400 hover:text-red-900 dark:hover:text-red-300 disabled:opacity-50"
                          title="Remove member"
                        >
                          <TrashIcon className="h-4 w-4" />
                        </button>
                      </PermissionGuard>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      )}

      {activeTab === 'settings' && (
        <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-6">
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-6">Organization Settings</h3>
          <div className="space-y-6">
            <div className="grid grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Timezone</label>
                <p className="mt-1 text-sm text-gray-900 dark:text-white">{organization?.settings.timezone}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Currency</label>
                <p className="mt-1 text-sm text-gray-900 dark:text-white">{organization?.settings.currency}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Date Format</label>
                <p className="mt-1 text-sm text-gray-900 dark:text-white">{organization?.settings.dateFormat}</p>
              </div>
            </div>
            
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <h4 className="text-sm font-medium text-gray-900 dark:text-white">Allow Public Projects</h4>
                  <p className="text-sm text-gray-500 dark:text-gray-400">Members can create public projects</p>
                </div>
                <div className={`relative inline-flex h-6 w-11 items-center rounded-full ${
                  organization?.settings.allowPublicProjects ? 'bg-blue-600' : 'bg-gray-200 dark:bg-gray-700'
                }`}>
                  <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition ${
                    organization?.settings.allowPublicProjects ? 'translate-x-6' : 'translate-x-1'
                  }`} />
                </div>
              </div>
              
              <div className="flex items-center justify-between">
                <div>
                  <h4 className="text-sm font-medium text-gray-900 dark:text-white">Require Approval</h4>
                  <p className="text-sm text-gray-500 dark:text-gray-400">New members require admin approval</p>
                </div>
                <div className={`relative inline-flex h-6 w-11 items-center rounded-full ${
                  organization?.settings.requireApproval ? 'bg-blue-600' : 'bg-gray-200 dark:bg-gray-700'
                }`}>
                  <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition ${
                    organization?.settings.requireApproval ? 'translate-x-6' : 'translate-x-1'
                  }`} />
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Invitations Tab */}
      {activeTab === 'invitations' && (
        <div className="bg-white dark:bg-gray-800 shadow rounded-lg">
          <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
            <h3 className="text-lg font-medium text-gray-900 dark:text-white">Invitations</h3>
            <PermissionGuard module="invitations" action="create">
              <button
                onClick={() => setShowAddMember(true)}
                className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700"
              >
                <PlusIcon className="h-4 w-4 mr-2" />
                Invite Member
              </button>
            </PermissionGuard>
          </div>
          <div className="divide-y divide-gray-200 dark:divide-gray-700">
            {invitationsLoading ? (
              <div className="p-6 text-center"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div></div>
            ) : !invitationsData?.invitations?.length ? (
              <div className="p-8 text-center text-sm text-gray-500 dark:text-gray-400">No invitations sent yet.</div>
            ) : (
              invitationsData.invitations.map((inv: Invitation & { roleId?: any; invitedBy?: any }) => (
                <div key={inv._id} className="px-6 py-4 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="h-9 w-9 rounded-full bg-indigo-100 dark:bg-indigo-900/30 flex items-center justify-center">
                      <EnvelopeIcon className="h-4 w-4 text-indigo-600 dark:text-indigo-400" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-900 dark:text-white">{inv.email}</p>
                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        Role: {(inv as any).roleId?.name || '—'} · Invited by {(inv as any).invitedBy?.name || '—'}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      inv.status === 'pending' ? 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-300'
                      : inv.status === 'accepted' ? 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300'
                      : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300'
                    }`}>{inv.status}</span>
                    {inv.status === 'pending' && (
                      <>
                        <PermissionGuard module="invitations" action="update">
                          <button
                            onClick={() => resendInviteMutation.mutate(inv._id)}
                            className="text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300" title="Resend"
                          >
                            <ArrowPathIcon className="h-4 w-4" />
                          </button>
                        </PermissionGuard>
                        <PermissionGuard module="invitations" action="delete">
                          <button
                            onClick={() => {
                              if (window.confirm(`Cancel the invitation to ${inv.email}?`)) {
                                cancelInviteMutation.mutate(inv._id);
                              }
                            }}
                            className="text-red-500 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300" title="Cancel"
                          >
                            <XMarkIcon className="h-4 w-4" />
                          </button>
                        </PermissionGuard>
                      </>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      )}

      {/* Add / Invite Member Modal */}
      {showAddMember && (
        <InviteMemberModal
          onClose={() => setShowAddMember(false)}
          onSubmit={(data) => sendInviteMutation.mutate(data)}
          isLoading={sendInviteMutation.isLoading}
          roles={rolesData?.roles || []}
        />
      )}

      {/* New Organization Modal */}
      {showNewOrganization && (
        <NewOrganizationModal
          onClose={() => {
            setShowNewOrganization(false);
            setNewOrgName('');
            setNewOrgCountry('');
            setSearchParams({});
          }}
          onSubmit={handleCreateOrganization}
          isLoading={isLoading}
          name={newOrgName}
          setName={setNewOrgName}
          country={newOrgCountry}
          setCountry={setNewOrgCountry}
        />
      )}

      {/* Edit Organization Modal */}
      {showEditOrg && organization && (
        <EditOrganizationModal
          organization={organization}
          onClose={() => setShowEditOrg(false)}
          onSubmit={(data) => updateOrgMutation.mutate(data)}
          isLoading={updateOrgMutation.isLoading}
        />
      )}
    </div>
  );
};

// New Organization Modal Component
const NewOrganizationModal: React.FC<{
  onClose: () => void;
  onSubmit: (e: React.FormEvent) => void;
  isLoading: boolean;
  name: string;
  setName: (value: string) => void;
  country: string;
  setCountry: (value: string) => void;
}> = ({ onClose, onSubmit, isLoading, name, setName, country, setCountry }) => {
  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-20 mx-auto p-5 border dark:border-gray-700 w-[28rem] shadow-lg rounded-md bg-white dark:bg-gray-800">
        <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-1">Create New Organization</h3>
        <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
          Create a new organization to manage separate projects, teams, and resources.
        </p>
        <form onSubmit={onSubmit} className="space-y-4">
          <div>
            <label htmlFor="new-org-name" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
              Organization Name *
            </label>
            <input
              id="new-org-name"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g., Acme Corp"
              className="mt-1 block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 text-sm"
              required
            />
          </div>
          <div>
            <label htmlFor="new-org-country" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
              Country *
            </label>
            <input
              id="new-org-country"
              type="text"
              value={country}
              onChange={(e) => setCountry(e.target.value)}
              placeholder="e.g., India"
              className="mt-1 block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 text-sm"
              required
            />
          </div>
          <div className="flex justify-end space-x-3 pt-1">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isLoading}
              className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50"
            >
              <PlusIcon className="h-4 w-4 mr-2" />
              {isLoading ? 'Creating...' : 'Create Organization'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Invite Member Modal Component
const InviteMemberModal: React.FC<{
  onClose: () => void;
  onSubmit: (data: { email: string; roleId: string; message?: string }) => void;
  isLoading: boolean;
  roles: Array<{ _id: string; name: string }>;
}> = ({ onClose, onSubmit, isLoading, roles }) => {
  const [email, setEmail] = useState('');
  const [roleId, setRoleId] = useState('');
  const [message, setMessage] = useState('');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!roleId) return;
    onSubmit({ email, roleId, message: message || undefined });
  };

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-20 mx-auto p-5 border dark:border-gray-700 w-[28rem] shadow-lg rounded-md bg-white dark:bg-gray-800">
        <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-1">Invite Team Member</h3>
        <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
          If the user already has an account they'll be prompted to join. Otherwise they'll receive a sign-up invite.
        </p>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Email address *</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              placeholder="colleague@example.com"
              className="mt-1 block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 text-sm"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Role *</label>
            <select
              value={roleId}
              onChange={(e) => setRoleId(e.target.value)}
              required
              className="mt-1 block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 text-sm"
            >
              <option value="">Select a role…</option>
              {roles.map(r => (
                <option key={r._id} value={r._id}>{r.name}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Personal message <span className="text-gray-400">(optional)</span></label>
            <textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              rows={2}
              maxLength={500}
              placeholder="Add a personal note to the invitation…"
              className="mt-1 block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 text-sm"
            />
          </div>
          <div className="flex justify-end space-x-3 pt-1">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isLoading || !roleId}
              className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50"
            >
              {isLoading ? 'Sending…' : 'Send Invitation'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Edit Organization Modal Component
const EditOrganizationModal: React.FC<{
  organization: Organization;
  onClose: () => void;
  onSubmit: (data: Partial<Organization>) => void;
  isLoading: boolean;
}> = ({ organization, onClose, onSubmit, isLoading }) => {
  const [formData, setFormData] = useState({
    name: organization.name,
    description: organization.description || '',
    industry: organization.industry || '',
    size: organization.size || 'startup',
    website: organization.website || '',
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit(formData);
  };

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-20 mx-auto p-5 border dark:border-gray-700 w-96 shadow-lg rounded-md bg-white dark:bg-gray-800">
        <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">Edit Organization</h3>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Name</label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              required
              className="mt-1 block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Description</label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              rows={3}
              className="mt-1 block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Industry</label>
            <input
              type="text"
              value={formData.industry}
              onChange={(e) => setFormData({ ...formData, industry: e.target.value })}
              className="mt-1 block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Size</label>
            <select
              value={formData.size}
              onChange={(e) => setFormData({ ...formData, size: e.target.value as any })}
              className="mt-1 block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="startup">Startup</option>
              <option value="small">Small</option>
              <option value="medium">Medium</option>
              <option value="large">Large</option>
              <option value="enterprise">Enterprise</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Website</label>
            <input
              type="url"
              value={formData.website}
              onChange={(e) => setFormData({ ...formData, website: e.target.value })}
              className="mt-1 block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
          <div className="flex justify-end space-x-3">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isLoading}
              className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50"
            >
              {isLoading ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default OrganizationPage;
